// Scannable QRIS code — renders the raw EMVCo payload as an actual QR image
// (the payload string alone cannot be scanned). Always ink-on-white regardless
// of theme: scanner apps need dark modules on a light background, so colors are
// pinned here and never inherit CSS variables (the one sanctioned white/black
// plate, ui/DESIGN.md §2). Level M + 4-module quiet zone per the QR spec
// recommendation for payment codes.

import { QRCodeSVG } from "qrcode.react";

export function QrisCode({ payload }: { payload: string }) {
  return (
    <div
      data-slot="qr-plate"
      className="w-fit rounded-lg bg-white p-3 ring-1 ring-line-strong"
      role="img"
      aria-label="Kode QR pembayaran QRIS"
    >
      <QRCodeSVG
        value={payload}
        size={224}
        level="M"
        marginSize={4}
        bgColor="#ffffff"
        fgColor="#000000"
        title="Kode QR pembayaran QRIS"
        className="block h-auto max-w-full"
      />
    </div>
  );
}

/**
 * Download the rendered QR as a PNG (e-wallet apps accept a gallery upload when
 * the buyer pays from the same device and can't scan their own screen). PNG,
 * not SVG: gallery pickers don't take vectors. Upscaled 4× ink-on-white so the
 * modules stay crisp after the gallery's recompression.
 */
export async function downloadQrisPng(container: HTMLElement | null, fileName: string): Promise<void> {
  const svg = container?.querySelector("svg");
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("QR render failed"));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
  });
  const scale = 4;
  const canvas = document.createElement("canvas");
  canvas.width = img.width * scale;
  canvas.height = img.height * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = false; // QR modules must stay hard-edged
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = fileName;
  a.click();
}
